/**
 * Organization editor — the uber-admin's screen for one client organization.
 *
 * Sits at the second level of the hierarchy (platform → ORGANIZATION → project →
 * feedback). It edits the things the product owner owns — the name, the plan,
 * and whether the org is active at all — and issues the organization-scoped MCP
 * token, which spans every project underneath.
 *
 * Plan and status are deliberately META_ADMIN-only: they are commercial levers,
 * and the alternative would be a half-permitted screen, which is exactly the
 * granular-permission model the hierarchy avoids. An org admin's own MCP tokens
 * live per project in the config editor.
 */
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { Organization, OrgPlan, OrgStatus } from '@hotline/contracts';

import { useAuth } from '../../auth/AuthContext.js';
import { useOrgScope } from '../../org/OrgScopeContext.js';
import { McpSection } from '../../components/McpSection.js';
import { Badge, Button, Field, Input, PageHeader, Select, Spinner } from '../../components/ui/index.js';
import { ErrorBanner } from '../../components/ui.js';
import { Icon } from '../../components/ui/icons.js';
import { toMessage, useAsync } from '../../lib/useAsync.js';

/** The three editable fields, held together so a draft is one value. */
interface Draft {
  name: string;
  plan: OrgPlan;
  status: OrgStatus;
}

const PLANS: OrgPlan[] = ['free', 'pro', 'enterprise'];
const STATUSES: OrgStatus[] = ['active', 'suspended'];

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="border-b border-border py-5 last:border-b-0">
      <h2 className="text-base font-bold text-text">{title}</h2>
      {description ? (
        <p className="mt-[3px] text-[12.5px] leading-relaxed text-muted">{description}</p>
      ) : null}
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </section>
  );
}

export default function OrgEditorPage(): ReactElement {
  const { orgId = '' } = useParams();
  const { client } = useAuth();
  const { setOrgId } = useOrgScope();
  const navigate = useNavigate();

  const org = useAsync<Organization>(
    () => client.api.getOrganization(orgId),
    [client, orgId],
  );

  /**
   * Edits are held as a draft that DEFAULTS to the loaded organization, rather
   * than as state seeded by an effect. Seeding in an effect renders one frame
   * with placeholder values before the real ones land — briefly showing a "free"
   * plan for an enterprise customer, which on a screen that sells plans is a
   * worse bug than it looks.
   */
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saved0: Draft | null = org.data
    ? { name: org.data.name, plan: org.data.plan, status: org.data.status }
    : null;
  const form = draft ?? saved0;
  const dirty =
    !!saved0 &&
    !!draft &&
    (draft.name !== saved0.name || draft.plan !== saved0.plan || draft.status !== saved0.status);

  const edit = (patch: Partial<Draft>): void => {
    if (!form) return;
    setSaved(false);
    setDraft({ ...form, ...patch });
  };

  const save = async (): Promise<void> => {
    if (!form) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await client.api.updateOrganization(orgId, {
        name: form.name.trim(),
        plan: form.plan,
        status: form.status,
      });
      setDraft(null);
      setSaved(true);
      await org.reload();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSaving(false);
    }
  };

  /** Act inside this organization — the projects/tickets screens below it. */
  const enter = (): void => {
    setOrgId(orgId);
    navigate('/org/instances');
  };

  const mcpApi = useMemo(
    () => ({
      scopeKey: `org:${orgId}`,
      list: () => client.api.listMcpTokensForOrg(orgId),
      create: (label: string) => client.api.createMcpTokenForOrg(orgId, label),
      revoke: (tokenId: string) => client.api.revokeMcpTokenForOrg(orgId, tokenId),
    }),
    [client, orgId],
  );

  if (org.loading) return <Spinner />;
  if (org.error || !org.data || !form) {
    return <ErrorBanner message={org.error ? toMessage(org.error) : 'Organization not found'} />;
  }

  return (
    <>
      <PageHeader
        leading={
          <Link to="/meta/orgs" className="text-muted hover:text-text">
            ← All organizations
          </Link>
        }
        title={org.data.name}
        description={`Organization · ${org.data.id}`}
        actions={
          <Button type="button" variant="secondary" size="sm" onClick={enter}>
            <Icon.eye size={14} />
            Enter organization
          </Button>
        }
      />

      {error ? <ErrorBanner message={error} /> : null}

      <Section
        title="General"
        description="The name clients see, the plan they are on, and whether the organization is active."
      >
        <Field label="Name" htmlFor="org-name">
          <Input
            id="org-name"
            value={form.name}
            onChange={(e) => edit({ name: e.target.value })}
            disabled={saving}
          />
        </Field>
        <Field label="Plan" htmlFor="org-plan">
          <Select
            id="org-plan"
            className="max-w-[200px]"
            value={form.plan}
            onChange={(e) => edit({ plan: e.target.value as OrgPlan })}
            disabled={saving}
          >
            {PLANS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Status"
          htmlFor="org-status"
          hint="Suspending an organization also cuts off its MCP tokens, including per-project ones."
        >
          <Select
            id="org-status"
            className="max-w-[200px]"
            value={form.status}
            onChange={(e) => edit({ status: e.target.value as OrgStatus })}
            disabled={saving}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-center gap-3">
          <Button type="button" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          {saved && !dirty ? <Badge tone="ok">Saved</Badge> : null}
        </div>
      </Section>

      <Section
        title="AI integration (MCP)"
        description="One connection string for an agent working across this organization."
      >
        <McpSection
          blurb="An organization token spans every project below. Hand the connection string to an AI agent and it can list the projects, fetch exact embed code for any of them, diagnose and add allowed origins, run a live end-to-end test, and report recent feedback across the whole organization. Project-only tokens live in each project's own Install screen."
          api={mcpApi}
        />
      </Section>
    </>
  );
}
